/**
 * Les conditions d'apparition de l'invitation à créer un compte, isolées de
 * React parce que ce sont elles, et non l'affichage, qui décident si quelqu'un
 * se fait interrompre.
 *
 * # Le moment, et pourquoi celui-là
 *
 * Le même que pour la capsule de retour, et pour la même raison : la fin de
 * session n'a pas de surface — `practice_session_ended` n'est émis qu'au
 * démontage de l'overlay ou à la fermeture de l'onglet, donc toujours au
 * moment où l'écran qui porterait l'invitation disparaît. Reste la **frontière
 * d'étape** : quand la FSM dépasse une phrase validée, `learn-piece-page` met
 * le transport en pause et fait défiler le roll. Les mains ont quitté les
 * touches, rien ne joue, la personne est là.
 *
 * # Pourquoi une seule phrase, là où le retour en demande deux
 *
 * Les deux invitations ne demandent pas la même chose. Le retour demande un
 * avis, donc il faut que la personne en ait un : deux phrases prouvent que la
 * boucle a tourné deux fois et que l'opinion porte sur la méthode. Ici on ne
 * demande rien du tout, on énonce un fait sur ce qui vient d'être gagné. Une
 * phrase validée suffit à ce que le fait existe.
 *
 * # Pourquoi deux minutes
 *
 * C'est le plancher qui sépare « j'ai ouvert pour voir » de « j'ai commencé
 * quelque chose ». En dessous, la seule phrase validée est presque toujours
 * celle du tutoriel, obtenue en quelques gestes : annoncer à quelqu'un qu'il
 * risque de perdre ça, c'est lui apprendre que ça ne vaut rien. Deux minutes
 * plutôt que les quatre du retour parce que cette invitation ne coûte pas
 * d'effort — elle se lit et se referme.
 *
 * # L'arbitrage avec la capsule de retour
 *
 * Les deux se posent au même endroit, au même instant, sur le même signal.
 * Elles ne doivent jamais se croiser, et le retour passe devant : il est rare
 * (une occasion par navigateur, définitive) et il ne se rattrape pas, alors
 * que l'invitation au compte est répétable et gagne en force avec le temps.
 *
 * D'où la règle, qui tient en une ligne de code : l'invitation au compte ne
 * s'arme que si la capsule de retour était **déjà consommée à l'ouverture de
 * la session**. Tant que le retour est dû, le compte se tait toute la session,
 * puisque le retour peut tomber à n'importe quelle frontière. Et comme le
 * retour est une occasion unique, la session où le compte parle est forcément
 * une session où le retour ne peut plus rien dire. Les deux contraintes —
 * jamais ensemble, jamais l'un juste après l'autre — tombent du même test, et
 * rien n'a besoin d'être ajouté au voisin.
 *
 * Le prix est connu : quelqu'un dont toutes les sessions durent moins de
 * quatre minutes ne déclenche jamais le retour, donc jamais l'invitation au
 * compte. C'est aussi quelqu'un qui n'a pas de progression à transporter.
 */

/** Une phrase validée : il y a quelque chose à perdre. */
export const MIN_VALIDATED = 1;

/** Deux minutes : la session a commencé, cf. l'en-tête. */
export const MIN_SESSION_MS = 2 * 60 * 1000;

/**
 * Place qu'il faut sous la playhead pour poser la capsule sans recouvrir des
 * notes à venir.
 *
 * Soixante, comme le voisin, et pour la même raison : une ligne de 18 px, plus
 * 20 px de rembourrage et les 12 px qui séparent la capsule du bas du roll. Ce
 * seuil a valu 96 tant que la phrase expliquait le stockage sur deux lignes ;
 * l'y laisser aurait fait taire l'invitation dans des situations où elle
 * tenait pourtant à l'écran.
 */
export const REQUIRED_ROOM_PX = 60;

export type AccountPromptContext = {
  /** Aucun compte ouvert, et l'authentification a fini de se résoudre. */
  readonly anonymous: boolean;
  /** Phrases au maximum `DONE`, tous passages confondus, pas seulement ceux du jour. */
  readonly phrasesValidated: number;
  /** Depuis le montage du parcours. */
  readonly sessionDurationMs: number;
  /**
   * La capsule de retour était-elle déjà consommée quand cette session a
   * commencé ? Lu une fois au montage et gelé : lire la valeur courante
   * laisserait le compte parler à la frontière suivant celle du retour.
   */
  readonly feedbackPromptSettled: boolean;
  /** Écran trop bas pour porter la capsule. */
  readonly shortViewport: boolean;
  /** Bande libre sous la playhead, en pixels. `0` quand elle est inconnue. */
  readonly playheadDistancePx: number;
};

/**
 * Faut-il inviter à la frontière d'étape qui vient d'être franchie ?
 *
 * Ne dit rien de la répétition ni du plafond : c'est
 * `account-prompt-storage` qui les porte, parce qu'ils dépendent du
 * navigateur et non de la session.
 */
export function isAccountPromptDue(context: AccountPromptContext): boolean {
  if (!context.anonymous) {
    return false;
  }

  if (!context.feedbackPromptSettled) {
    return false;
  }

  if (context.phrasesValidated < MIN_VALIDATED) {
    return false;
  }

  if (context.sessionDurationMs < MIN_SESSION_MS) {
    return false;
  }

  // Un téléphone couché n'a pas la place de porter la capsule, et dépenser une
  // des trois occasions sur un écran où l'on ne peut pas y répondre
  // confortablement serait du gaspillage. Rien n'est consigné : la frontière
  // suivante, sur un écran normal, retentera.
  if (context.shortViewport) {
    return false;
  }

  // Idem quand la bande sous la playhead est trop courte : on attend plutôt
  // que de poser la capsule sur des notes à venir.
  return !(
    context.playheadDistancePx > 0 && context.playheadDistancePx < REQUIRED_ROOM_PX
  );
}
